import { api, transformPaginatedResponse } from "@/lib/api";
import type {
  Event,
  CreateEventDto,
  UpdateEventDto,
  EventStatistics,
  CompanyOverview,
  EventTimeline,
} from "@/types/event";
import type { PaginatedResponse } from "@/types/common";

interface EventsResponse extends PaginatedResponse<Event> {
  success: boolean;
}

/** Backend GET /events/:id/statistics response (before ResponseInterceptor wrap) */
interface BackendStatisticsPayload {
  eventName?: string;
  statistics: {
    guestCount?: number;
    checkedInGuestCount?: number;
    totalGuests: number;
    checkedInGuests: number;
    checkInRate: number;
  };
  busiestCheckInTimeRange?: { timeRange: string | null; count: number };
  hourlyBreakdown?: Array<{ hour: string; count: number }>;
  guestListStatistics: Array<{
    id: string;
    name: string;
    totalGuests: number;
    checkedInGuests: number;
    checkInRate: number;
  }>;
  userStatistics: Array<{
    user: { id: string; name: string; role: string };
    statistics: {
      totalGuests: number;
      checkedInGuests: number;
      checkInPercentage: number;
    };
  }>;
  invitationStatistics?: Array<{
    id: string;
    title: string | null;
    type: string;
    totalGuests: number;
    checkedInGuests: number;
    checkInRate: number;
  }>;
}

class EventService {
  /**
   * Get all events with pagination and filtering
   */
  async getAll(options?: {
    page?: number;
    limit?: number;
    filter?: "all" | "upcoming" | "past";
    includeTimestamps?: boolean;
  }): Promise<EventsResponse> {
    const params = new URLSearchParams();
    if (options?.page) params.append("page", String(options.page));
    if (options?.limit) params.append("limit", String(options.limit));
    if (options?.filter) params.append("filter", options.filter);
    if (options?.includeTimestamps) params.append("include", "timestamps");

    const query = params.toString();
    const response = await api.get<{
      data?: Event[] | { data?: Event[]; meta?: unknown };
      meta?: unknown;
      pagination?: unknown;
    }>(`/events${query ? `?${query}` : ""}`);
    return transformPaginatedResponse<Event>(response, options);
  }

  /**
   * Get a single event by ID
   */
  async getById(id: string): Promise<Event> {
    return api.get<Event>(`/events/${id}`);
  }

  /**
   * Get events for a specific venue
   */
  async getByVenue(
    venueId: string,
  ): Promise<{ success: boolean; data: Event[] }> {
    return api.get<{ success: boolean; data: Event[] }>(
      `/events/venue/${venueId}`,
    );
  }

  /**
   * Get my events
   */
  async getMyEvents(): Promise<EventsResponse> {
    return api.get<EventsResponse>("/events/my-events");
  }

  /**
   * Get events for a specific company
   */
  async getByCompany(companyId: string): Promise<EventsResponse> {
    return api.get<EventsResponse>(`/events/company/${companyId}`);
  }

  /**
   * Create a new event
   */
  async create(data: CreateEventDto): Promise<Event> {
    return api.post<Event>("/events", data);
  }

  /**
   * Update an event
   */
  async update(id: string, data: UpdateEventDto): Promise<Event> {
    return api.patch<Event>(`/events/${id}`, data);
  }

  /**
   * Delete an event
   */
  async delete(id: string): Promise<Event> {
    return api.delete<Event>(`/events/${id}`);
  }

  /**
   * Get event statistics. Unwraps backend response and maps to frontend EventStatistics type.
   */
  async getStatistics(id: string): Promise<EventStatistics> {
    const raw = await api.get<BackendStatisticsPayload>(
      `/events/${id}/statistics`,
    );
    const stats = raw.statistics;
    return {
      eventId: id,
      eventName: raw.eventName ?? "",
      guestCount: stats.guestCount ?? stats.totalGuests,
      checkedInGuestCount: stats.checkedInGuestCount ?? stats.checkedInGuests,
      totalGuests: stats.totalGuests,
      checkedIn: stats.checkedInGuests,
      notCheckedIn: stats.totalGuests - stats.checkedInGuests,
      checkInPercentage: stats.checkInRate,
      guestListStats: (raw.guestListStatistics ?? []).map((list) => ({
        id: list.id,
        name: list.name,
        totalGuests: list.totalGuests,
        checkedIn: list.checkedInGuests,
        percentage: list.checkInRate,
      })),
      busiestHour: raw.busiestCheckInTimeRange?.timeRange ?? null,
      busiestHourCount: raw.busiestCheckInTimeRange?.count ?? 0,
      hourlyBreakdown: raw.hourlyBreakdown ?? [],
      byUser: (raw.userStatistics ?? []).map((u) => ({
        userId: u.user.id,
        userName: u.user.name,
        userRole: u.user.role,
        totalGuests: u.statistics.totalGuests,
        checkIns: u.statistics.checkedInGuests,
        checkInPercentage: u.statistics.checkInPercentage,
      })),
      byInvitation: (raw.invitationStatistics ?? []).map((inv) => ({
        id: inv.id,
        title: inv.title,
        type: inv.type,
        totalGuests: inv.totalGuests,
        checkedIn: inv.checkedInGuests,
        percentage: inv.checkInRate,
      })),
    };
  }

  /**
   * Company-wide door-watch overview (per-event check-in totals + summary).
   */
  async getCompanyOverview(): Promise<CompanyOverview> {
    return api.get<CompanyOverview>("/events/statistics/overview");
  }

  /**
   * Cumulative check-in timeline for one event (live fill curve + first/last check-in).
   */
  async getTimeline(
    id: string,
    interval: "5m" | "15m" | "1h" = "15m",
  ): Promise<EventTimeline> {
    return api.get<EventTimeline>(
      `/events/${id}/statistics/timeline?interval=${interval}`,
    );
  }

  /**
   * Export event statistics as Excel file
   */
  async exportStatistics(id: string): Promise<Blob> {
    const apiUrl =
      process.env.NEXT_PUBLIC_API_URL || "http://localhost:3001/api";
    const response = await fetch(`${apiUrl}/events/${id}/statistics/export`, {
      credentials: "include",
    });

    if (!response.ok) {
      throw new Error("Export failed");
    }

    return response.blob();
  }

  /**
   * Download exported statistics
   */
  async downloadStatistics(id: string, eventName: string): Promise<void> {
    const blob = await this.exportStatistics(id);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    // Etkinlik adı dosya adına giriyor: aksi halde indirilen dosyalar
    // birbirinden ayırt edilemiyor. Türkçe harfler ve noktalama tek bir tireye
    // iniyor, uçtaki tireler atılıyor.
    const slug = eventName
      .replace(/[^\p{L}\p{N}]+/gu, "-")
      .replace(/^-+|-+$/g, "");
    const day = new Date().toISOString().split("T")[0];
    a.download = `${slug || "etkinlik"}-gece-raporu-${day}.xlsx`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }
}

export const eventService = new EventService();
